/**
 * Alert Detail Modal
 *
 * Displays comprehensive alert information including full timeline,
 * client/therapist/supervisor details, and SLA tracking.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import adminAlertApi from '../../services/adminAlertApi';

const PRIORITY_COLORS = {
  critical: '#d32f2f',
  urgent: '#f57c00',
  high: '#ffa726',
  medium: '#42a5f5',
  low: '#66bb6a'
};

const STATUS_COLORS = {
  pending: '#ff9800',
  acknowledged: '#2196f3',
  resolved: '#4caf50',
  dismissed: '#9e9e9e'
};

const AlertDetailModal = ({ open, onClose, alertId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState(null);
  const [supervisors, setSupervisors] = useState([]);

  useEffect(() => {
    if (open && alertId) {
      loadAlertDetails();
    }
  }, [open, alertId]);

  const loadAlertDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminAlertApi.getAlertDetails(alertId);
      setAlert(data.alert);
      setSupervisors(data.supervisors || []);
    } catch (err) {
      console.error('[AlertDetailModal] Error loading alert details:', err);
      setError(err.message || 'Failed to load alert details');
    } finally {
      setLoading(false);
    }
  };

  const getSLAStatus = () => {
    if (!alert || !alert.sla || !alert.sla.responseDeadline) {
      return { status: 'unknown', text: 'N/A', color: '#9e9e9e', icon: null };
    }

    const now = new Date();
    const deadline = new Date(alert.sla.responseDeadline);
    const isActive = ['pending', 'acknowledged'].includes(alert.status);

    if (isActive && deadline < now) {
      const breachTime = Math.floor((now - deadline) / 60000);
      return {
        status: 'breached',
        text: `Breached ${breachTime} minutes ago`,
        color: '#d32f2f',
        icon: <ErrorIcon />
      };
    }

    if (isActive && deadline < new Date(now.getTime() + 15 * 60 * 1000)) {
      const remaining = Math.floor((deadline - now) / 60000);
      return {
        status: 'warning',
        text: `${remaining} minutes remaining`,
        color: '#ff9800',
        icon: <WarningIcon />
      };
    }

    if (alert.status === 'resolved' || alert.status === 'dismissed') {
      return {
        status: 'completed',
        text: `Resolved in ${alert.sla.timeToResolve || 'N/A'} minutes`,
        color: '#4caf50',
        icon: <CheckCircleIcon />
      };
    }

    return {
      status: 'on_track',
      text: 'On track',
      color: '#4caf50',
      icon: <CheckCircleIcon />
    };
  };

  const renderTimeline = () => {
    if (!alert || !alert.timeline || alert.timeline.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No timeline data available
        </Typography>
      );
    }

    return (
      <Timeline position="right">
        {alert.timeline.map((event, index) => (
          <TimelineItem key={index}>
            <TimelineOppositeContent color="textSecondary" sx={{ flex: 0.3 }}>
              <Typography variant="caption">
                {format(new Date(event.timestamp), 'MMM d, h:mm a')}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot color={event.status === 'resolved' ? 'success' : 'primary'} />
              {index < alert.timeline.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Typography variant="body2" fontWeight="bold">
                {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {event.note}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    );
  };

  if (!open) return null;

  const slaStatus = alert ? getSLAStatus() : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Alert Details</Typography>
          <Button onClick={onClose} startIcon={<CloseIcon />}>
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : alert ? (
          <Box>
            {/* Alert Status Banner */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: PRIORITY_COLORS[alert.priority], color: 'white' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6">
                    {alert.priority.toUpperCase()} PRIORITY
                  </Typography>
                  <Typography variant="body2">
                    {alert.metadata?.crisisType?.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} textAlign="right">
                  <Chip
                    label={alert.status.toUpperCase()}
                    style={{
                      backgroundColor: STATUS_COLORS[alert.status],
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* SLA Tracking */}
            {slaStatus && (
              <Paper sx={{ p: 2, mb: 3, backgroundColor: slaStatus.color + '20', borderLeft: `4px solid ${slaStatus.color}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {slaStatus.icon}
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      SLA Status: {slaStatus.text}
                    </Typography>
                    <Typography variant="caption">
                      Target Response: {alert.sla?.targetResponseTime} minutes |
                      Deadline: {alert.sla?.responseDeadline
                        ? format(new Date(alert.sla.responseDeadline), 'MMM d, h:mm a')
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Assignment Information */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Assignment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Client
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {alert.clientId?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {alert.clientId?.email || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Therapist
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {alert.therapistId?.name || 'Unassigned'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {alert.therapistId?.email || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {supervisors.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Clinical Supervisors
                    </Typography>
                    {supervisors.map((supervisor, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <GroupIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {supervisor.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {supervisor.email}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Service Group
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {alert.serviceGroupId?.name || 'N/A'}
                  </Typography>
                  {alert.serviceGroupId?.slug && (
                    <Typography variant="caption" color="textSecondary">
                      {alert.serviceGroupId.slug}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>

            {/* Concern Details */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Concern Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Type
                  </Typography>
                  <Typography variant="body2">
                    {alert.concern?.concernType || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Explanation
                  </Typography>
                  <Typography variant="body2">
                    {alert.concern?.explanation || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Suggested Action
                  </Typography>
                  <Chip
                    label={alert.concern?.suggestedAction?.replace(/_/g, ' ') || 'N/A'}
                    size="small"
                    color="primary"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Timeline */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Timeline
              </Typography>
              {renderTimeline()}
            </Paper>

            {/* Detection Metadata */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detection Metadata
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Source
                  </Typography>
                  <Typography variant="body2">
                    {alert.metadata?.source?.replace(/_/g, ' ') || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Detection Time
                  </Typography>
                  <Typography variant="body2">
                    {alert.metadata?.detectionTimestamp
                      ? format(new Date(alert.metadata.detectionTimestamp), 'MMM d, yyyy h:mm a')
                      : format(new Date(alert.createdAt), 'MMM d, yyyy h:mm a')}
                  </Typography>
                </Grid>
                {alert.conversationId && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Conversation ID
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {alert.conversationId}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertDetailModal;
